import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import {
  BranchWithStats,
  CreateBranchInput,
  UpdateBranchInput,
  createBranch,
  setBranchActive,
  updateBranch,
  useBranchList,
} from "../api/branches";
import { useAuth } from "../auth/AuthContext";
import {
  ActivePassiveFilterSelect,
  DEFAULT_ACTIVE_PASSIVE_FILTER,
  activePassiveToOptionalBoolean,
  type ActivePassiveFilter,
} from "../components/common/ActivePassiveFilterSelect";
import { UI_LOADING } from "../constants/uiLabels";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./BranchesPage.css";

type ModalState =
  | { mode: "create" }
  | { mode: "edit"; branch: BranchWithStats }
  | null;

function BranchModal(props: {
  initial?: BranchWithStats;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { initial, onClose, onSaved } = props;
  const isEdit = !!initial;

  const [name, setName] = useState(initial?.name ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(initial?.name ?? "");
    setAddress(initial?.address ?? "");
    setPhone(initial?.phone ?? "");
    setEmail(initial?.email ?? "");
    setIsActive(initial?.is_active ?? true);
    setError(null);
  }, [initial]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isEdit && initial) {
        const body: UpdateBranchInput = {
          name: name.trim(),
          address: address.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
        };
        await updateBranch(initial.id, body);
        if (isActive !== initial.is_active) {
          await setBranchActive(initial.id, isActive);
        }
      } else {
        const body: CreateBranchInput = {
          name: name.trim(),
          address: address.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
        };
        await createBranch(body);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kayit Başarısız.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kp-branch-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-branch-modal">
        <div className="kp-branch-modal__header">
          <h3>{isEdit ? "Şube Düzenle" : "Yeni Sube"}</h3>
          <button type="button" className="kp-branch-modal__close" onClick={onClose}>
            x
          </button>
        </div>
        <form onSubmit={onSubmit} className="kp-branch-modal__body">
          <label className="kp-branch-field">
            <span>
              Şube Adi <em>*</em>
            </span>
            <input
              type="text"
              maxLength={150}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={busy}
            />
          </label>
          <label className="kp-branch-field">
            <span>Adres</span>
            <textarea
              rows={2}
              maxLength={1000}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={busy}
            />
          </label>
          <div className="kp-branch-modal__row">
            <label className="kp-branch-field">
              <span>Telefon</span>
              <input
                type="text"
                maxLength={30}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="kp-branch-field">
              <span>E-posta</span>
              <input
                type="email"
                maxLength={150}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </label>
          </div>

          {isEdit && (
            <div className="kp-branch-field kp-form-status">
              <span>Durum</span>
              <label className="kp-form-status__toggle">
                <input
                  type="checkbox"
                  checked={isActive}
                  disabled={busy}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span>{isActive ? "Aktif sube" : "Pasif sube"}</span>
              </label>
              <small className="kp-form-status__hint">
                Pasifleştirme, subede aktif kayit varsa engellenir.
              </small>
            </div>
          )}

          {error && <div className="kp-branch-modal__error">{error}</div>}

          <div className="kp-branch-modal__actions">
            <button
              type="button"
              className="kp-btn"
              onClick={onClose}
              disabled={busy}
            >
              Vazgeç
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn--primary"
              disabled={busy || !name.trim()}
            >
              {busy ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BranchStatLink(props: {
  count: number;
  branchId: string;
  path: string;
  label: string;
  linked: boolean;
}) {
  const { count, branchId, path, label, linked } = props;
  const content = (
    <>
      <strong>{count}</strong> {label}
    </>
  );
  if (!linked || count <= 0) {
    return (
      <span className={count <= 0 ? "kp-branches__stat--empty" : undefined} title={label}>
        {content}
      </span>
    );
  }
  return (
    <Link
      to={`${path}?branch_id=${encodeURIComponent(branchId)}`}
      className="kp-branches__stat-link"
      title={`${label} listesini filtrele`}
    >
      {content}
    </Link>
  );
}

export function BranchesPage() {
  const { session } = useAuth();
  const role = session?.user?.role;
  const isPlatformOwner = session?.user?.is_platform_owner === true;
  const canManage = role === "tenant_admin" && isPlatformOwner;

  const [filterActive, setFilterActive] = useState<ActivePassiveFilter>(
    DEFAULT_ACTIVE_PASSIVE_FILTER,
  );
  const isActive = activePassiveToOptionalBoolean(filterActive);
  const list = useBranchList({ isActive });

  const [modal, setModal] = useState<ModalState>(null);
  const [busyId] = useState<string | null>(null);
  const [pageError] = useState<string | null>(null);

  const branches = list.data || [];
  const activeCount = branches.filter((b) => b.is_active).length;

  useRegisterListPageStats({
    label: "şube",
    visible: branches.length,
    loading: list.loading,
  });

  return (
    <div className="kp-branches">
      <div className="kp-branches__header">
        <div>
          <h1>Şubeler</h1>
          <p>
            Kurumunuzun subelerini yonetin. Toplam {branches.length} sube; {activeCount} aktif.
            {!canManage && " Salt okuma yetkiniz var."}
          </p>
        </div>
        <div className="kp-branches__header-actions">
          <button className="kp-btn" onClick={list.reload} disabled={list.loading}>
            Yenile
          </button>
          {canManage && (
            <button
              className="kp-btn kp-btn--primary"
              onClick={() => setModal({ mode: "create" })}
            >
              + Yeni Sube
            </button>
          )}
        </div>
      </div>

      <div className="kp-branches__toolbar kp-card">
        <ActivePassiveFilterSelect value={filterActive} onChange={setFilterActive} />
      </div>

      {pageError && <div className="kp-card kp-error">{pageError}</div>}
      {list.error && <div className="kp-card kp-error">{list.error}</div>}

      <div className="kp-card kp-branches__table-wrap">
        {list.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {!list.loading && branches.length === 0 && (
          <div className="kp-empty">Bu filtreyle eslesen sube yok.</div>
        )}
        {!list.loading && branches.length > 0 && (
          <table className="kp-branches__table kp-data-table">
            <thead>
              <tr>
                <th>Sube</th>
                <th>Adres</th>
                <th>Iletisim</th>
                <th>Kayitlar</th>
                <th>Durum</th>
                {canManage && <th></th>}
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id} className={!b.is_active ? "kp-branches__row--passive" : ""}>
                  <td>
                    <strong>{b.name}</strong>
                  </td>
                  <td>
                    {b.address ? (
                      <span className="kp-branches__cell-text">{b.address}</span>
                    ) : (
                      <span className="kp-muted">-</span>
                    )}
                  </td>
                  <td>
                    {b.phone && <div>{b.phone}</div>}
                    {b.email && <div className="kp-muted">{b.email}</div>}
                    {!b.phone && !b.email && <span className="kp-muted">-</span>}
                  </td>
                  <td>
                    <div className="kp-branches__stats">
                      <BranchStatLink
                        count={b.total_students}
                        branchId={b.id}
                        path="/students"
                        label="kursiyer"
                        linked={isPlatformOwner}
                      />
                      <BranchStatLink
                        count={b.total_instructors}
                        branchId={b.id}
                        path="/instructors"
                        label="egitmen"
                        linked={isPlatformOwner}
                      />
                      <BranchStatLink
                        count={b.total_vehicles}
                        branchId={b.id}
                        path="/vehicles"
                        label="arac"
                        linked={isPlatformOwner}
                      />
                      <BranchStatLink
                        count={b.total_users}
                        branchId={b.id}
                        path="/users"
                        label="kullanici"
                        linked={isPlatformOwner}
                      />
                    </div>
                  </td>
                  <td>
                    <span
                      className={`kp-branches__badge ${
                        b.is_active
                          ? "kp-branches__badge--active"
                          : "kp-branches__badge--passive"
                      }`}
                    >
                      {b.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  {canManage && (
                    <td>
                      <div className="kp-branches__actions">
                        <button
                          type="button"
                          className="kp-btn kp-btn--ghost"
                          onClick={() => setModal({ mode: "edit", branch: b })}
                          disabled={busyId === b.id}
                        >
                          Düzenle
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <BranchModal
          initial={modal.mode === "edit" ? modal.branch : undefined}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            list.reload();
          }}
        />
      )}
    </div>
  );
}
